import { Box } from '@chakra-ui/react'
import { generatePath, useNavigate } from 'react-router'
import { Paths } from 'src/constants/paths'
import { useAuthenticatedAccount } from 'src/features/auth/context'
import { useModal } from 'src/hooks/use-modal'
import { useQuery } from 'src/hooks/use-query'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import {
  AddExplanationScreen_ReferredPatientDocument,
  CreateExplanationDocument,
} from 'src/lib/gql-client'
import {
  useGqlMutation,
  useGqlQuerySWRNoSuspense,
} from 'src/lib/gql-client/request'

import { ExplanationForm } from '../ExplanationForm/ExplanationForm'

export const AddExplanationScreen: React.FC = () => {
  const toast = useMirohaToast()

  const navigate = useNavigate()

  const {
    account: { selectedTrial, selectedTrialHospitalUid },
  } = useAuthenticatedAccount()

  const { request: createExplanation } = useGqlMutation(
    CreateExplanationDocument,
    {
      onSuccess: ({ createExplanation: data }) => {
        toast({
          status: 'success',
          title: '登録が完了しました',
        })
        navigate(
          generatePath(Paths.Explanation, {
            trialUid: selectedTrial.uid,
            explanationUid: data.explanationUid,
          }),
        )
      },
    },
  )

  const { showModal } = useModal()

  const query = useQuery()

  const referredPatientUid = query.get('referredPatientUid') ?? undefined

  const { data } = useGqlQuerySWRNoSuspense(
    AddExplanationScreen_ReferredPatientDocument,
    !!referredPatientUid ? { referredPatientUid } : null,
    { revalidateIfStale: false },
  )

  const referredPatient = data?.referredPatient

  if (!!referredPatientUid && !referredPatient) {
    return null
  }

  return (
    <Box as="main" h="full">
      <ExplanationForm
        formType="add"
        referredPatient={referredPatient}
        onSubmit={schema => {
          showModal({
            title: '同意説明を登録',
            content: '同意説明を登録します',
            submitText: '登録する',
            onSubmit: () => {
              if (!selectedTrialHospitalUid) {
                throw new Error('selectedTrialHospitalUid is not defined')
              }
              createExplanation({
                input: {
                  patient: {
                    candidateId: schema.candidateId,
                    expDiseaseId: schema.diseaseId,
                    mobileNumber: schema.phoneNumber,
                    email: schema.email,
                  },
                  revision: {
                    partnerTrialHospitalUid: schema.partnerTrialHospitalUid,
                    docSets: schema.docSetUids.map((icfDocumentSetUid, i) => ({
                      icfDocumentSetUid: icfDocumentSetUid,
                      index: i,
                    })),
                    members: schema.members.map(member => ({
                      trialHospitalUid: member.trialHospitalUid,
                      trialMemberUid: member.member.uid,
                    })),
                    withRepresentative: false,
                    type: schema.type,
                  },
                  trialHospitalUid: selectedTrialHospitalUid,
                  trialPatientUid: referredPatient?.trialPatientUid,
                },
              })
            },
          })
        }}
      />
    </Box>
  )
}
